import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { LifeBuoy, Loader2 } from "lucide-react";
import LogoBSE from "@/assets/logo/favicon_logo_bse.webp";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/login", { email, password });
      login(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name}`);
      navigate(["admin", "super_admin", "technician"].includes(data.user.role) ? "/admin" : "/dashboard");
    } catch (err) {
      setError(err.response?.data
        ? apiError(err.response.data)
        : "Unable to sign in. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const quick = (em, pw) => {
    setEmail(em);
    setPassword(pw);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between text-white p-12 relative overflow-hidden bg-[linear-gradient(150deg,#0a1f6b_0%,#1e40af_45%,#3b1e9e_100%)]">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-0 -left-16 h-80 w-80 rounded-full bg-cyan-400/20 blur-3xl" />
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-md bg-white/15 flex items-center justify-center">
            <img src={LogoBSE} alt="BSE Logo" className="h-8 w-8 rounded-md object-cover" />
          </div>
          <span className="font-heading font-extrabold tracking-tight text-xl">
            Brothersindo Saudara Emas
          </span>
        </div>
        <div>
          <h1 className="font-heading font-black tracking-tighter text-5xl leading-none mb-6">
            Ticketing & SLA Management
          </h1>
          <p className="text-primary-foreground/80 text-base leading-relaxed max-w-md">
            Report product issues, track resolution progress in real time, and
            monitor SLA performance from a single command center.
          </p>
        </div>
        <div className="text-xs uppercase tracking-[0.2em] text-primary-foreground/60">
          Support Operations Platform
        </div>
      </div>

      <div className="flex items-center justify-center p-6 bg-transparent">
        <form
          onSubmit={submit}
          className="w-full max-w-sm space-y-5 glass-strong rounded-2xl p-8"
          data-testid="login-form"
        >
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center">
              <LifeBuoy className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-extrabold tracking-tight text-xl">
              Brothersindo
            </span>
          </div>
          <div>
            <h2 className="font-heading font-bold tracking-tight text-3xl">
              Sign in
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Access your support dashboard
            </p>
          </div>

          {error && (
            <div
              className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2"
              role="alert"
              aria-live="polite"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email"
              placeholder="you@company.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password"
              placeholder="••••••••"
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={loading}
            data-testid="login-submit"
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Sign
            in
          </Button>

          <p className="text-sm text-center text-muted-foreground">
            Need an account? Please contact your administrator.
          </p>

          
        </form>
      </div>
    </div>
  );
}
